import { useRef, useEffect } from 'react';
import classNames from 'classnames';
import noop from 'lodash/fp/noop';

import ProgressBarStep from './ProgressBarStep';
import './ProgressBar.scss';

type Step = {
	id: string;
	label: string;
	isComplete: boolean;
	isSelected: boolean;
};

type Props = {
	steps: Step[];
	onChange?: (stepId: string) => void;
};

export default function ProgressBar({ steps = [], onChange = noop }: Props) {
	const containerElRef = useRef<HTMLDivElement>(null);
	const checkmarkRef = useRef<HTMLDivElement>(null);

	const isAllCompleted = steps.every(step => step.isComplete);

	const progressBarClassName = classNames('progress-bar', {
		'progress-bar--is-all-completed': isAllCompleted,
	});

	useEffect(() => {
		const containerEl = containerElRef?.current;
		const checkmarkEl = checkmarkRef?.current;

		if (containerEl) {
			const translateX =
				isAllCompleted && checkmarkEl
					? ((checkmarkEl.offsetLeft - containerEl.offsetWidth / 2 + checkmarkEl.offsetWidth / 2) /
							containerEl.offsetWidth) *
						100
					: 0;

			containerEl.style.transform = `translateX(-${translateX}%)`;
		}

		return () => {
			if (containerEl) {
				containerEl.style.removeProperty('transform');
			}
		};
	}, [isAllCompleted]);

	return (
		<div className={progressBarClassName}>
			<div className="progress-bar__inner-container" ref={containerElRef}>
				{steps.map((step, index) => {
					const isFinalStep = index === steps.length - 1;
					return (
						<ProgressBarStep
							key={step.id}
							label={step.label}
							step={index + 1}
							isSelected={step.isSelected}
							isCompleted={step.isComplete}
							isFinalStep={isFinalStep}
							isAllCompleted={isAllCompleted}
							onClick={step.isComplete ? () => onChange(step.id) : undefined}
							ref={isFinalStep && isAllCompleted ? checkmarkRef : null}
						/>
					);
				})}
			</div>
		</div>
	);
}
